import { useCallback } from 'react';
import { useLocale } from '../../context/LocaleContext';
import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { calculateFinalPriceModifier } from '../../utilities/priceModifier';
import { getPriceModifiersForVariant } from '../../utilities/resolveComponentGroupForVariant';
import { getFontDisplayName } from '../../utilities/getFontDisplayName';
import { buildGroupedSlotModel, inferActiveGroupName } from '../../utilities/buildGroupedSlotModel';
import { getShareConfig, hasImageUploadSlot } from '../../utilities/shareConfig';
import { findUvInsertTextListElement } from '../../utilities/deriveStoryCardText';
import { getZeroPriceLabel } from '../../utilities/getZeroPriceLabel';

/**
 * What the Story of Us keepsake card holds, as the summary lists it (ALBAU-907 AC8). The cart
 * drawer no longer describes the card: it lists it in "Personalised (N)" as its image (ALBAU-957).
 */
const STORY_CARD_SUMMARY = 'Months, moments, photo & message';

/**
 * The £0 keepsake card's price line in the summary, matching the cart drawer's "Included"
 * (`theme/snippets/cart-item-row.liquid`). Gadget greys out Fee Description on UV insert slots,
 * so admin cannot set this word on the slot itself.
 */
const STORY_CARD_PRICE_LABEL = 'Included';

/**
 * Custom hook for PersonalisationSummaryModal logic and className selection.
 * @param {Object} props - Props for the view model.
 * @param {function} [props.onClose] - Callback for closing the modal.
 * @returns {Object} View model for the summary modal component.
 */
export function useViewModel({ onClose } = {}) {
  const {
    product,
    personalisationSteps,
    personalisationSlots,
    selections,
    variants,
    componentGroups,
    currentTotalPrice,
    isSharedPersonalisation,
    setCurrentStepId,
    updateSelections,
    resolvedUvInserts
  } = usePersonalisationContext();
  const { formatPrice } = useLocale();

  // Resolved share copy (theme settings) for shared-design guidance (ALBAU-578).
  const shareConfig = getShareConfig();

  /**
   * Finds which step a slot belongs to.
   * @param {string} slotId - The ID of the slot to find the step for.
   * @returns {string|null} - The step ID that contains the slot, or null if not found.
   */
  const findStepForSlot = (slotId) => {
    if (!personalisationSteps || !slotId) return null;

    for (const [stepId, step] of personalisationSteps.entries()) {
      if (step.slots && step.slots.includes(slotId)) {
        return stepId;
      }
    }
    return null;
  };

  /**
   * Whether a selection is the Story of Us keepsake card: a UV_INSERT whose config has a
   * TEXT_LIST, which no other insert has.
   * @param {Object} slot - The slot object.
   * @param {*} selectedValue - The slot's selection.
   * @returns {boolean} - True for the keepsake card.
   */
  const isStoryCardSelection = (slot, selectedValue) => {
    if (slot.slotType !== 'UV_INSERT' || typeof selectedValue !== 'object' || selectedValue === null) return false;
    const uvConfig = resolvedUvInserts?.configs.find((c) => c.handle === selectedValue.configHandle);
    return Boolean(uvConfig && findUvInsertTextListElement({ config: uvConfig }));
  };

  /**
   * Gets the display value for a slot selection.
   * @param {Object} slot - The slot object.
   * @param {string|string[]|Object} selectedValue - The selected value(s).
   * @returns {string} - The display value for the selection.
   */
  const getSlotDisplayValue = (slot, selectedValue) => {
    if (!selectedValue) return '';

    // Handle file uploads - check if selectedValue is a File object.
    if (selectedValue instanceof File) {
      const groupNameSuffix =
        slot.groupName && typeof slot.groupName === 'string' && slot.groupName.trim() !== ''
          ? slot.groupName.trim()
          : '';
      if (groupNameSuffix) {
        return `${selectedValue.name}<br>(${groupNameSuffix})`;
      }
      return selectedValue.name;
    }

    // Handle alphanumeric slots with font selection (object format)
    if (slot.slotType === 'ALPHANUMERIC' && typeof selectedValue === 'object' && selectedValue.text) {
      let displayText = selectedValue.text;

      // If translation is enabled and we have translated text, show both values
      if (
        slot.translateToLanguage &&
        selectedValue.translatedText &&
        selectedValue.translatedText !== selectedValue.text
      ) {
        displayText = `${selectedValue.text} / ${selectedValue.translatedText}`;
      }

      const groupNameSuffix =
        slot.groupName && typeof slot.groupName === 'string' && slot.groupName.trim() !== ''
          ? slot.groupName.trim()
          : '';

      // Show text and font if font selection is available.
      if (slot.allowedFonts && slot.allowedFonts.length > 0 && selectedValue.font) {
        const fontLabel = getFontDisplayName({ fontName: selectedValue.font });
        if (groupNameSuffix) {
          return `${displayText}<br>(${fontLabel}, ${groupNameSuffix})`;
        }
        return `${displayText}<br>(${fontLabel})`;
      }

      if (groupNameSuffix) {
        return `${displayText}<br>(${groupNameSuffix})`;
      }

      return displayText;
    }

    if (slot.slotType === 'CHECKBOX') {
      // Handle checkbox slots - show custom text if available, otherwise show default
      return slot.checkboxSummaryValue || 'Accepted';
    }

    if (slot.slotType === 'STAR_MAP' && typeof selectedValue === 'object' && selectedValue !== null) {
      const location = selectedValue.location || selectedValue.locationPlace?.formattedAddress || '';
      const datetime = selectedValue.datetime || '';
      const time = selectedValue.time || '';
      const dateTimeLabel = [datetime, time].filter(Boolean).join(' ');

      if (location && dateTimeLabel) {
        return `${location}<br>${dateTimeLabel}`;
      }
      return location || dateTimeLabel || '-';
    }

    if (slot.slotType === 'LOCATION_MAP' && typeof selectedValue === 'object' && selectedValue !== null) {
      const location = selectedValue.location || selectedValue.locationPlace?.formattedAddress || '';
      return location || '-';
    }

    if (slot.slotType === 'COMPONENT' || slot.slotType === 'BASE_CONFIG') {
      // Handle arrays for upsells
      if (Array.isArray(selectedValue)) {
        const variantTitles = selectedValue
          .map((variantId) => {
            const variant = variants.get(variantId);

            // Get component group info if available
            let groupPrefix = '';
            if (variant?.componentGroupId) {
              const componentGroup = componentGroups.get(variant.componentGroupId);
              if (componentGroup) {
                groupPrefix = componentGroup.uiHeading || componentGroup.internalName;
              }
            }

            const variantTitle = variant ? variant.title : variantId;
            // Filter out "Default Title" as it's not useful for single variants
            const cleanVariantTitle = variantTitle === 'Default Title' ? '' : variantTitle;
            return groupPrefix && cleanVariantTitle
              ? `${groupPrefix} / ${cleanVariantTitle}`
              : groupPrefix || cleanVariantTitle;
          })
          .filter(Boolean);

        return variantTitles.join('<br>');
      }

      // Handle single values
      const variant = variants.get(selectedValue);
      if (variant) {
        // Get component group info if available
        let groupPrefix = '';
        if (variant.componentGroupId) {
          const componentGroup = componentGroups.get(variant.componentGroupId);
          if (componentGroup) {
            groupPrefix = componentGroup.uiHeading || componentGroup.internalName;
          }
        }

        const variantTitle = variant.title;
        // Filter out "Default Title" as it's not useful for single variants
        const cleanVariantTitle = variantTitle === 'Default Title' ? '' : variantTitle;
        return groupPrefix && cleanVariantTitle
          ? `${groupPrefix} / ${cleanVariantTitle}`
          : groupPrefix || cleanVariantTitle;
      }
      return String(selectedValue);
    }

    // UV_INSERT: selectedValue is { variantId, configHandle, textByKey, imageByKey, ... }.
    // Show the chosen design's title, with the first non-empty text input appended if present.
    if (slot.slotType === 'UV_INSERT' && typeof selectedValue === 'object' && selectedValue !== null) {
      // The keepsake card is described, not quoted: its first text is a month, which reads as
      // noise on its own.
      if (isStoryCardSelection(slot, selectedValue)) return STORY_CARD_SUMMARY;
      const uvConfig = resolvedUvInserts?.configs.find((c) => c.handle === selectedValue.configHandle);
      const uvTheme = uvConfig?.themes.find((t) => Number(t.id) === selectedValue.variantId);
      const themeName = uvTheme?.title || uvConfig?.handle || '';
      const firstText = Object.values(selectedValue.textByKey || {}).find((v) => v && v.trim() !== '');
      return firstText ? `${themeName}<br>${firstText}` : themeName || '-';
    }

    if (typeof selectedValue === 'object' && selectedValue !== null) {
      if (selectedValue.text) return String(selectedValue.text);
      if (selectedValue.location) return String(selectedValue.location);
      if (selectedValue.locationPlace?.formattedAddress) return String(selectedValue.locationPlace.formattedAddress);
      if (selectedValue.formattedAddress) return String(selectedValue.formattedAddress);
      return '-';
    }

    return String(selectedValue);
  };

  /**
   * Gets the price for a slot selection, applying price modifiers from component groups.
   * @param {string|string[]} selectedValue - The selected variant ID(s).
   * @param {string|null|undefined} preferredComponentGroupId - Group id stored on the slot selection.
   * @returns {number} - The price in pounds.
   */
  const getSlotPrice = (selectedValue, preferredComponentGroupId, slot) => {
    if (!selectedValue) return 0;
    if (slot?.isFreeSlot) return 0;
    if (typeof slot?.overridePrice === 'number') {
      return Array.isArray(selectedValue)
        ? selectedValue.filter(Boolean).length * slot.overridePrice
        : slot.overridePrice;
    }

    // UV_INSERT: price comes from the resolved UV insert config themes, not the variants Map.
    // `selectedValue` is a UvInsertSlotSelection object for UV_INSERT slots, not a variant ID string.
    if (
      slot?.slotType === 'UV_INSERT' &&
      typeof selectedValue === 'object' &&
      selectedValue !== null &&
      !Array.isArray(selectedValue)
    ) {
      if (slot.isFreeSlot) return 0;
      const uvSel = /** @type {{ variantId: number, configHandle: string }} */ (selectedValue);
      const uvConfig = resolvedUvInserts?.configs.find((c) => c.handle === uvSel.configHandle);
      const uvTheme = uvConfig?.themes.find((t) => Number(t.id) === uvSel.variantId);
      if (!uvTheme?.price) return 0;
      const variantPricePounds = parseFloat(String(uvTheme.price)) / 100;
      return variantPricePounds;
    }

    // IMAGE_UPLOAD photo-insert add-on: the +£X per photo lives on the slot's linked
    // photoVariant SKU, not in the variants Map (selectedValue is the uploaded File).
    // Mirror the journey total / cart-transform pricing — ALBAU-799.
    if (slot?.slotType === 'IMAGE_UPLOAD' && slot.photoVariant) {
      return typeof slot.photoVariantPrice === 'number' ? slot.photoVariantPrice : 0;
    }

    // Handle arrays for grouped products.
    if (Array.isArray(selectedValue)) {
      return selectedValue.reduce((total, variantId) => {
        const variant = variants.get(variantId);
        if (!variant || !variant.price) return total;

        const { priceModifierType, priceModifierValue } = getPriceModifiersForVariant(
          componentGroups,
          variantId,
          preferredComponentGroupId
        );

        // Calculate the final price with modifier applied.
        const originalPrice =
          typeof variant.price.amount === 'string' ? Number(variant.price.amount) : variant.price.amount;
        const finalPrice = calculateFinalPriceModifier({
          priceModifierType,
          priceModifierValue,
          variantPrice: originalPrice
        });

        return total + finalPrice;
      }, 0);
    }

    // Handle single values.
    const variant = variants.get(selectedValue);
    if (!variant || !variant.price) return 0;

    const { priceModifierType, priceModifierValue } = getPriceModifiersForVariant(
      componentGroups,
      selectedValue,
      preferredComponentGroupId
    );

    // Calculate the final price with modifier applied.
    const originalPrice =
      typeof variant.price.amount === 'string' ? Number(variant.price.amount) : variant.price.amount;
    const finalPrice = calculateFinalPriceModifier({
      priceModifierType,
      priceModifierValue,
      variantPrice: originalPrice
    });

    return finalPrice;
  };

  /**
   * Navigates to the step containing the specified slot.
   * @param {string} slotId - The ID of the slot to navigate to.
   */
  const navigateToSlot = (slotId) => {
    if (!slotId || !setCurrentStepId) return;

    const stepId = findStepForSlot(slotId);
    if (stepId) {
      setCurrentStepId(stepId);
    }
  };

  /**
   * Handles the edit button click for a summary item.
   * @param {string} slotId - The ID of the slot to navigate to.
   */
  const handleEditClick = (slotId) => {
    navigateToSlot(slotId);
    // Close the summary modal after navigation.
    if (onClose) {
      onClose();
    }
  };

  /**
   * Handles removing an invalid selection from a slot.
   * @param {string} slotId - The ID of the slot to remove the selection from.
   */
  const handleRemoveSelection = useCallback(
    (slotId) => {
      const selection = selections.slots?.find((slot) => slot.id === slotId);
      if (!selection) return;

      const slotData = personalisationSlots.get(slotId);
      if (!slotData) return;

      // For arrays, filter out invalid variants.
      // For single values, set to null.
      const currentValue = selection.selected;
      let newValue = null;

      if (Array.isArray(currentValue)) {
        // Filter out invalid variants.
        const validVariants = currentValue.filter((variantId) => {
          if (!variantId) return false;
          const variant = variants.get(variantId);
          return variant && variant.availableForSale !== false;
        });
        newValue = validVariants.length > 0 ? validVariants : null;
      }

      updateSelections({
        slotId,
        itemSelected: newValue
      });
    },
    [selections, personalisationSlots, variants, updateSelections]
  );

  /**
   * Creates summary items from all available slots, showing both selected and unselected items.
   * Orders slots according to Map insertion order to match the original personalisation flow.
   * This ensures the summary matches the order users see in the personalisation flow.
   * @returns {Array} - Array of summary items with slot info, selections, and prices.
   */
  const getSummaryItems = () => {
    if (!personalisationSlots || !personalisationSteps) return [];

    // Get all steps in Map insertion order to match the personalisation flow
    const orderedSteps = Array.from(personalisationSteps.entries());

    // Collect all slots in the order they appear in steps
    const orderedSlots = [];

    orderedSteps.forEach(([stepId, step]) => {
      if (step.slots && Array.isArray(step.slots)) {
        let groupedActiveGroupName = null;
        if (step.slotMode === 'GROUPED') {
          const stepSlotObjects = step.slots.map((id) => personalisationSlots.get(id)).filter(Boolean);
          const { optionGroups } = buildGroupedSlotModel(stepSlotObjects);
          groupedActiveGroupName = inferActiveGroupName({
            optionGroups,
            selections: selections.slots || []
          });
        }

        step.slots.forEach((slotId) => {
          const slotData = personalisationSlots.get(slotId);
          if (slotData) {
            // Find the selection for this slot
            const selection = selections.slots?.find((slot) => slot.id === slotId);
            const selectedValue = selection ? selection.selected : null;
            const isSelected = selectedValue !== null && selectedValue !== '';

            // For GROUPED steps, always skip slots not in the active group.
            // This applies to both text (cleared on switch) and image upload slots
            // (propagated to all groups — we filter by group name, not by selection presence).
            if (step.slotMode === 'GROUPED' && slotData.groupName && slotData.groupName !== groupedActiveGroupName) {
              return;
            }
            const displayValue = isSelected && selectedValue ? getSlotDisplayValue(slotData, selectedValue) : '';
            const price =
              isSelected && selectedValue ? getSlotPrice(selectedValue, selection?.componentGroupId, slotData) : 0;

            // Derive inline validation for shared journeys.
            let isInvalid = false;
            let invalidReason = '';
            let showRemoveOption = false;

            if (isSharedPersonalisation && isSelected) {
              if (slotData.slotType === 'COMPONENT' || slotData.slotType === 'BASE_CONFIG') {
                const value = selectedValue;
                const checkIds = Array.isArray(value) ? value : [value];
                for (const variantId of checkIds) {
                  if (!variantId) continue;
                  const variant = variants.get(variantId);
                  if (!variant) {
                    isInvalid = true;
                    invalidReason = 'This option is currently unavailable - please select another to continue';
                    showRemoveOption = true;
                    break;
                  }
                  if (variant.availableForSale === false) {
                    isInvalid = true;
                    invalidReason = 'This option is currently out of stock - please select another to continue';
                    showRemoveOption = true;
                    break;
                  }
                }
              }
            }

            // Slot-level guidance for a shared design: prompt the recipient to add
            // their own image on image-upload slots (uploaded images aren't shared).
            const guidanceText =
              isSharedPersonalisation && slotData.slotType === 'IMAGE_UPLOAD' ? shareConfig.slotImageNote : '';

            orderedSlots.push({
              id: slotData.id,
              stepId,
              title: slotData.summaryLabel || slotData.uiHeading || 'Personalisation',
              selectedValue: displayValue,
              price,
              slotType: slotData.slotType,
              isSelected,
              overridePrice: slotData.overridePrice,
              isFreeSlot: slotData.isFreeSlot,
              // Lets the modal word a £0 line the way admin worded it ("Included") instead
              // of printing "+£0.00" - the same field the UV insert slot already reads. The
              // keepsake card's slot cannot hold one, so it gets the drawer's word.
              feeDescription:
                isSelected && isStoryCardSelection(slotData, selectedValue)
                  ? getZeroPriceLabel({ slot: slotData, fallback: STORY_CARD_PRICE_LABEL })
                  : slotData.feeDescription,
              isInvalid,
              invalidReason,
              showRemoveOption,
              guidanceText
            });
          }
        });
      }
    });

    return orderedSlots;
  };

  const productTitle = product?.title || 'Summary';
  const summaryItems = getSummaryItems();
  // Panel-level guidance shown to a recipient who opened a shared design.
  // Image-upload products get an extra note prompting the recipient to add their own images.
  let sharedPersonalisationText = null;
  if (isSharedPersonalisation) {
    sharedPersonalisationText = shareConfig.recipientMessage;
    if (hasImageUploadSlot(personalisationSlots) && shareConfig.recipientImageNote) {
      sharedPersonalisationText = `${shareConfig.recipientMessage} ${shareConfig.recipientImageNote}`;
    }
  }

  return {
    productTitle,
    summaryItems,
    currentTotalPrice,
    sharedPersonalisationText,
    navigateToSlot,
    handleEditClick,
    handleRemoveSelection,
    formatPrice
  };
}
